import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom"; // v3.4.590
import { projects as projectsApi } from "../services/api";
import toast from "react-hot-toast";
import DocxPreview from "../components/DocxPreview"; // v3.4.557 — معاينة Word دقيقة
import { FiFileText, FiPlus, FiPrinter, FiDownload, FiEye, FiX, FiEdit2, FiCheck } from "react-icons/fi";
import "./ContractsPage.css";
import { LOCKED_FIELDS, FormatPicker, WafiForm, DirectForm, VariablesReview, GoogleDocPanel, FORMAT_LABEL, STATUS_LABEL, contractsGoogle, pdfUrl, invalidateTemplateConfig } from "../components/ContractGoogleTools"; // v3.4.523 — Google Docs / v3.4.525 — حقول المباشر
import { paginateBlocks } from "../utils/a4Pagination"; // v3.4.594 — ترقيم موحّد مع محرر القالب
import { buildContractDocx, loadContractFonts } from "../utils/contractDocx"; // v3.4.600 — ملف Word من المعاينة (يحترمه Google: A4 + فواصل الصفحات)
import ContractTemplatesEditor from "../components/ContractTemplatesEditor"; // v3.4.530 — تعديل قوالب العقود
import ResaleDocsTab, { ResaleTemplateEditor } from "../components/ResaleDocsTab"; // v3.4.722 — تفويض إعادة بيع / مخالصة إعادة بيع · v3.4.732 — محررا القالبين في تبويب «القوالب»

// ══════════════════════════════════════════════════════════════
// Contracts Module - إنشاء عقود المشاريع
// ══════════════════════════════════════════════════════════════

const API_BASE = (typeof window !== "undefined" ? window.location.origin : ""); // v3.4.482 — نفس العنوان المفتوح (نطاق DO أو erp.rael.com.sa)

export default function ContractsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && !user?.impersonating;

  const [activeTab, setActiveTab] = useState("create");
  // v3.4.722 — التبويبات الرئيسية الثلاث (بطلب المالك): عقود شراء (كل ما سبق) / تفويض إعادة بيع / مخالصة إعادة بيع — تُحفظ في الرابط ?doc=
  const [mainTab, setMainTab] = useState(() => { try { const d = new URLSearchParams(window.location.search).get("doc"); return d === "authorization" || d === "settlement" || d === "templates" ? d : "purchase"; } catch { return "purchase"; } });
  const MAIN_TABS = [["purchase", "📄 عقود شراء", "#2563eb"], ["authorization", "📝 تفويض إعادة بيع", "#7c3aed"], ["settlement", "🧾 مخالصة إعادة بيع", "#0f766e"], ["templates", "🛠 القوالب", "#b45309"]]; // v3.4.732 — تبويب القوالب (عقود + تفويض + مخالصة) لمن يملك صلاحية تعديل القوالب
  const pickMain = (k) => { setMainTab(k); try { const u = new URL(window.location.href); if (k === "purchase") u.searchParams.delete("doc"); else u.searchParams.set("doc", k); window.history.replaceState(null, "", u.toString()); } catch { /* */ } }; // create | history
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitDetails, setUnitDetails] = useState(null);
  const [reservationDetails, setReservationDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [contractContent, setContractContent] = useState("");
  const [editableContent, setEditableContent] = useState("");
  const [contractVariables, setContractVariables] = useState({});
  const [existingContract, setExistingContract] = useState(null); // عقد موجود للوحدة
  const [contractTemplate, setContractTemplate] = useState(null); // القالب من قاعدة البيانات
  // v3.4.523 — صيغة العقد (مباشر / وافي) والمسودة في Google Docs
  const [format, setFormat] = useState("direct");
  const [directVars, setDirectVars] = useState(null); // v3.4.528 — القيم الحيّة من نموذج العقد المباشر
  // v3.4.530 — محرر القوالب: للأدمن (خارج المعاينة) أو صلاحية contracts_can_edit_templates
  const canEditTemplates = isAdmin || !!user?.contracts_can_edit_templates;
  const [tplVersion, setTplVersion] = useState(0);
  const [showDraftPreview, setShowDraftPreview] = useState(false);
  const [wafiPreview, setWafiPreview] = useState(null); // v3.4.538 — v3.4.557: القيم الحالية؛ المعاينة تُرسم من ملف Word المملوء بـ docx-preview (تنسيق وافي كما هو)
  const [wafiBusy] = useState(false);
  const previewWafi = (vars) => { setWafiPreview((p) => (p ? null : { vars: vars || {}, at: Date.now() })); };
  const [gBusy, setGBusy] = useState(false);
  const [srcOpen, setSrcOpen] = useState({ unit: false, res: false }); // v3.4.600 — أقسام «بيانات المصدر في النظام»
  const [reEdit, setReEdit] = useState(false); // v3.4.591 — «تعديل إلكتروني للعقد»: الرجوع لخطوة الحقول ثم حفظ المسودة من جديد
  useEffect(() => { setReEdit(false); }, [selectedUnit, existingContract?.id]);
  const draftVars = () => { const o = { ...((existingContract && existingContract.variables) || {}) }; delete o._pageSettings; return o; };
  // v3.4.600 — المسودة أُنشئت على وحدة غير وحدة الحجز الحالية (تغيّرت الوحدة بعدها): حقول الوحدة تُؤخذ حديثة عند التعديل، ويُعاد ربط العقد عند الحفظ
  const unitMismatch = !!(existingContract && selectedUnit && existingContract.unit_id && Number(existingContract.unit_id) !== Number(selectedUnit));
  const UNIT_KEYS = { direct: ["Lot#", "FlatType#", "Floor#", "Front#", "Area#", "Rooms#", "RestR#", "LivingR#", "Kitchen#", "Maid#", "ProjectName", "UnitLabel"], wafi: ["project_name", "city", "district", "unit_type", "unit_no", "building_no", "floor_no", "unit_area"] };
  const freshLocked = (fmt, fresh) => Object.fromEntries((LOCKED_FIELDS[fmt] || []).filter((k) => fresh && fresh[k] != null && String(fresh[k]).trim() !== "").map((k) => [k, fresh[k]])); // v3.4.630
  const freshUnit = (fmt, fresh) => (unitMismatch ? Object.fromEntries(UNIT_KEYS[fmt].filter((k) => fresh && fresh[k] !== undefined).map((k) => [k, fresh[k]])) : {});
  const startReEdit = () => { setFormat(existingContract?.format === "wafi" ? "wafi" : "direct"); setShowDraftPreview(false); setWafiPreview(null); setReEdit(true); };
  const canApprove = isAdmin || !!user?.contracts_can_edit || !!user?.can_edit_info;
  const canDelDraft = canApprove || !!user?.contracts_can_add || !!user?.can_create; // v3.4.629
  const prepareWafiVariables = () => {
    const client = reservationDetails?.linked_clients?.[0] || {};
    const proj = projects.find(p => p.id === selectedProject) || {};
    const today = new Date(); const days = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
    const price = Number(reservationDetails?.value || unitDetails?.price || 0);
    const remaining = unitDetails?.amount_remaining != null ? Number(unitDetails.amount_remaining) : null;
    const loc = String(proj.location || "");
    return {
      day: days[today.getDay()], contract_date: today.toLocaleDateString("ar-SA"), place: "جدة",
      /* v3.4.634 — بيانات المشتري كلها من ملف العميل */ buyer_name: client.name || reservationDetails?.client_name || "", buyer_nationality: client.nationality || "", buyer_id: client.national_id || client.client_number || "", buyer_phone: client.landline || "", buyer_mobile: client.phone || "", buyer_address: client.national_address || "", buyer_email: client.email || "",
      project_name: proj.name || "", city: /درب الحرمين|جدة/.test(loc) ? "جدة" : "", district: loc,
      unit_type: unitDetails?.unit_type || "شقة", unit_facilities: "موقف", unit_no: unitDetails?.unit_label || "", building_no: unitDetails?.building_name || "", floor_no: unitDetails?.floor_number != null ? String(unitDetails.floor_number) : "",
      unit_area: unitDetails?.area_sqm ? `${unitDetails.area_sqm}م2` : "", land_share: "", common_share: "", yard_area: "",
      price: price ? price.toLocaleString("en-US") : "", price_words: price ? numberToArabicWords(price) + " ريال" : "",
      remaining: remaining != null ? (remaining > 0 ? remaining.toLocaleString("en-US") : "صفر") : "", /* v3.4.637 — عقد وافي: العربون 0 (يُعامل منفصلًا ويُسترد) والرسوم الإدارية افتراضيًا 5% من الثمن (وإن كان لعرض السعر المعتمد رسوم تُؤخذ منه في النموذج) */ deposit: "0", admin_fee: price ? (Math.round(price * 5) / 100).toLocaleString("en-US") : "",
    };
  };
  // v3.4.597 — إرسال العقد المباشر إلى Google Docs بنسخة مطابقة لصفحات المعاينة؛ وإن زاد عدد صفحات PDF عن المعاينة يُصغَّر النص 4% ويُعاد (حتى 3 مرات)
  const sendDirectToGoogle = async (variables, html, existingId) => {
    const psx = variables && variables._pageSettings;
    const prep = async (s) => { try { return psx && psx.v2 && !psx.legacy ? await buildContractDocx(html, psx, s) : null; } catch (e) { console.warn("[contract docx]", e); return null; } };
    let shrink = 1, g = await prep(shrink);
    const body = (extra) => ({ variables, html, unit_id: selectedUnit, ...(g ? { docx_base64: g.base64, expect_pages: g.pages } : {}), ...(extra || {}) });
    const first = existingId ? await contractsGoogle.regenerate(existingId, body()) : await contractsGoogle.createDraft({ format: "direct", unit_id: selectedUnit, reservation_id: effResId, project_id: selectedProject, ...body() });
    let r = first, tries = 0;
    while (g && r.pdf_pages && r.pdf_pages > g.pages && tries < 3) {
      tries++; shrink = Math.round((shrink - 0.04) * 100) / 100; g = await prep(shrink); if (!g) break;
      r = await contractsGoogle.regenerate(r.contract.id, body({ fit_retry: tries }));
    }
    if (g && r.pdf_pages && r.pdf_pages !== g.pages) toast(`⚠️ مستند Google يخرج في ${r.pdf_pages} صفحات والمعاينة ${g.pages} — راجع المستند قبل الطباعة`, { duration: 8000 });
    return { ...first, contract: r.contract, pdf_pages: r.pdf_pages };
  };
  const createGoogleDraft = async (fmt, variables, html) => {
    setGBusy(true);
    try {
      if (reEdit && existingContract) { // v3.4.591 — حفظ المسودة من جديد في نفس مستند Google
        if (!window.confirm("حفظ المسودة من جديد بالحقول المعدّلة؟\n\nسيُستبدل محتوى مستند Google الحالي بالمحتوى المولَّد من الحقول — أي تعديل حر سابق داخل Google Docs سيُفقد.")) { setGBusy(false); return; }
        const r = fmt === "direct" ? await sendDirectToGoogle(variables, html, existingContract.id) : await contractsGoogle.regenerate(existingContract.id, { variables, html, unit_id: selectedUnit });
        toast.success(r.changed ? `💾 حُفظت المسودة من جديد — ${r.changed} حقل معدّل` : "💾 حُفظت المسودة من جديد");
        setExistingContract(r.contract); setReEdit(false); setPreviewMode(false); setShowDraftPreview(false); setWafiPreview(null);
        setGBusy(false); return;
      }
      const r = fmt === "direct" ? await sendDirectToGoogle(variables, html, null) : await contractsGoogle.createDraft({ format: fmt, unit_id: selectedUnit, reservation_id: effResId, project_id: selectedProject, variables, html }); // v3.4.590 — v3.4.597
      toast.success("📝 أُنشئت مسودة العقد في Google Docs" + (r.shared ? " ومُنحت صلاحية التعديل" : ""));
      setExistingContract(r.contract); setPreviewMode(false);
      if (r.contract.google_doc_link) window.open(r.contract.google_doc_link, "_blank", "noopener");
    } catch (e) { toast.error(e.message || "تعذّر إنشاء المستند"); }
    setGBusy(false);
  };
  const refreshExisting = async () => { try { const res = await fetch(`${API_BASE}/api/contracts/unit/${selectedUnit}${effResId ? `?reservation=${effResId}` : ""}`, { credentials: "include" }); const d = await res.json(); setExistingContract(d.contract || null); } catch (_) { /* */ } };

  // جلب قالب العقد
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/contract-templates/type/unit_sale`, {
          credentials: "include"
        });
        if (res.ok) {
          const data = await res.json();
          setContractTemplate(data.template);
        }
      } catch (e) {
        console.error("Error fetching contract template:", e);
      }
    };
    fetchTemplate();
  }, [tplVersion]); // v3.4.530 — يُعاد الجلب بعد حفظ القالب من المحرر

  // v3.4.590 — اختيار الحجز (بداية رحلة العقد) + فتح مباشر بـ ?reservation=ID من «تنسيق الحجوزات»
  const [searchParams, setSearchParams] = useSearchParams();
  const [resList, setResList] = useState(null);
  const [resQ, setResQ] = useState("");
  const [pickedRes, setPickedRes] = useState(null);
  const effResId = (pickedRes && pickedRes.unit_id === selectedUnit) ? pickedRes.reservation_id : unitDetails?.linked_reservation_id;
  // v3.4.619 — عرض السعر المعتمد المربوط بالحجز: منه نظام الدفعات وجدولها (وافي: الملحق 3، المباشر: خيار السداد)
  const approvedQuoteId = pickedRes && pickedRes.unit_id === selectedUnit && pickedRes.quote_id && pickedRes.quote_status === "approved" ? pickedRes.quote_id : null;
  const normQ = (s) => String(s || "").toLowerCase().replace(/[أإآ]/g, "ا").replace(/ة/g, "ه").replace(/ى/g, "ي");
  const filteredRes = (resList || []).filter(r => !resQ.trim() || normQ(`#${r.reservation_id} ${r.reservation_id} ${r.client_name} ${r.client_phone} ${r.project_name} ${r.unit_label} ${r.unit_number} ${r.building_name}`).includes(normQ(resQ.trim())));
  // v3.4.591 — تصنيف الحجوزات حسب حالة العقد: none (بلا عقد) / draft (مسودة) / unsigned (معتمد بانتظار التوقيع) / signed (معتمد وموقّع)
  const RES_TABS = [["pending", "⏳ حجوزات بانتظار إنشاء عقود", [["none", "بلا عقد"], ["draft", "مسودة تحت الإجراء"]]], ["approved", "✅ عقود معتمدة", [["unsigned", "بانتظار التوقيع"], ["signed", "معتمدة وموقّعة"]]]];
  const [resTab, setResTab] = useState("pending");
  const [resSub, setResSub] = useState("all"); // v3.4.592 — «الكل» هو الفلتر الافتراضي داخل كل تاب
  const resCat = (r) => (!r.contract_id ? "none" : r.contract_status === "approved" ? (r.signed_at ? "signed" : "unsigned") : "draft");
  const resCounts = filteredRes.reduce((m, r) => { const k = resCat(r); m[k] = (m[k] || 0) + 1; return m; }, {});
  const tabCats = ((RES_TABS.find(t => t[0] === resTab) || RES_TABS[0])[2]).map(([k]) => k);
  const subRes = filteredRes.filter(r => (resSub === "all" ? tabCats.includes(resCat(r)) : resCat(r) === resSub));
  const shownRes = subRes.slice(0, 60);
  const resCatChip = (r) => { const k = resCat(r); return k === "none" ? <span className="rp-no">بلا عقد</span>
    : k === "draft" ? <span className="rp-has draft">📝 مسودة — تحت الإجراء</span>
    : k === "unsigned" ? <span className="rp-has unsigned">✅ معتمد — بانتظار التوقيع</span>
    : <span className="rp-has signed" title={r.signed_at ? "وُقّع " + new Date(r.signed_at).toLocaleDateString("en-CA") : ""}>✍️ معتمد وموقّع</span>; };
  // v3.4.629 — حذف مسودة عقد من القائمة (تُنقل لسلة المحذوفات ومستند Google إلى سلة Drive)
  const deleteDraft = async (r) => {
    if (!window.confirm(`حذف مسودة عقد الحجز #${r.reservation_id} — ${r.client_name || ""} (${r.unit_label || ""})؟\nتُنقل المسودة إلى سلة المحذوفات ومستند Google إلى سلة Drive.`)) return;
    try { const res = await fetch(`${API_BASE}/api/contracts/${r.contract_id}/delete-draft`, { method: "POST", credentials: "include" }); const d = await res.json().catch(() => ({})); if (!res.ok) throw new Error(d.error || "فشل الحذف");
      toast.success("حُذفت مسودة العقد"); loadResList(); } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { loadContractFonts(); }, []); // v3.4.630 — Aref Ruqaa / Markazi Text للمعاينة وملف Word
  const loadResList = useCallback(() => fetch(`${API_BASE}/api/contracts/reservations`, { credentials: "include" }).then(r => r.json()).then(d => {
    const list = d.reservations || []; setResList(list);
    setPickedRes(p => (p ? (list.find(x => x.reservation_id === p.reservation_id && x.unit_id === p.unit_id) || p) : p));
  }).catch(() => setResList([])), []);
  const [autoEdit, setAutoEdit] = useState(false); // v3.4.595 — «تعديل المسودة» من القائمة يفتح التعديل الإلكتروني مباشرة
  useEffect(() => {
    if (!autoEdit || !existingContract || existingContract.unit_id !== selectedUnit) return;
    setAutoEdit(false);
    if (existingContract.status === "draft") { startReEdit(); setTimeout(() => { const el = document.querySelector(".reedit-bar"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 400); }
  }, [autoEdit, existingContract, selectedUnit]); // eslint-disable-line
  // v3.4.596 — أعمدة عرض السعر المرتبط بالحجز
  const CT_LABEL = { direct: "مباشر", wafi: "وافي" };
  const fmtSar = (v) => Math.round(Number(v) || 0).toLocaleString("en-US");
  const resExtra = (r) => {
    const ct = r.quote_contract_type || r.contract_format || null;
    const first = r.quote_first_amount != null ? Number(r.quote_first_amount) : null;
    return {
      type: ct ? (CT_LABEL[ct] || ct) : null, typeFrom: r.quote_contract_type ? "من عرض السعر" : r.contract_format ? "من العقد" : "",
      first, firstPct: r.quote_first_pct != null ? Number(r.quote_first_pct) : null,
      expected: first == null && r.expected_payment_amount != null ? Number(r.expected_payment_amount) : null,
      n: r.quote_id ? Number(r.quote_installments) || 0 : null,
    };
  };
  const pickRes = (r, edit = false) => {
    setAutoEdit(!!edit);
    if (!r.contract_id && r.quote_contract_type) setFormat(r.quote_contract_type === "wafi" ? "wafi" : "direct"); // صيغة العقد من عرض السعر المرتبط
    setPickedRes(r); setSelectedUnit(null); setPreviewMode(false); setSelectedProject(Number(r.project_id));
    setSearchParams({ reservation: String(r.reservation_id) }, { replace: true });
  };
  const clearPicked = () => { setPickedRes(null); setSelectedUnit(null); setSelectedProject(null); setSearchParams({}, { replace: true }); };
  useEffect(() => { loadResList(); }, [loadResList, existingContract?.id, existingContract?.status, existingContract?.signed_at]); // v3.4.591 — تتحدّث القوائم بعد الإنشاء/الاعتماد/التوقيع
  const wantRes = searchParams.get("reservation");
  useEffect(() => {
    if (!wantRes || !resList) return;
    if (pickedRes && String(pickedRes.reservation_id) === wantRes) return;
    const r = resList.find(x => String(x.reservation_id) === wantRes);
    if (r) { setActiveTab("create"); pickRes(r); }
    else toast.error(`الحجز #${wantRes} غير متاح لإنشاء عقد — وحدته ليست بحالة «محجوز»`);
  }, [wantRes, resList]); // eslint-disable-line
  useEffect(() => { if (pickedRes && units.some(u => u.id === pickedRes.unit_id)) setSelectedUnit(pickedRes.unit_id); }, [pickedRes, units]);

  // جلب قائمة المشاريع
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await projectsApi.list();
        setProjects(res.data.projects || res.data || []);
      } catch (e) {
        console.error(e);
        toast.error("فشل جلب المشاريع");
      }
    };
    fetchProjects();
  }, []);

  // جلب قائمة الوحدات عند اختيار مشروع
  useEffect(() => {
    if (!selectedProject) {
      setUnits([]);
      setSelectedUnit(null);
      return;
    }
    const fetchUnits = async () => {
      try {
        const res = await projectsApi.get(selectedProject);
        const projectUnits = res.data.units || [];
        // فقط الوحدات المحجوزة
        setUnits(projectUnits); // v3.4.591 — كل الوحدات: الحجز المختار يحدّد الوحدة (قد تكون «مباع» بعد العقد المعتمد)
      } catch (e) {
        console.error(e);
      }
    };
    fetchUnits();
  }, [selectedProject]);

  // جلب تفاصيل الوحدة والحجز المرتبط
  useEffect(() => {
    if (!selectedUnit) {
      setUnitDetails(null);
      setReservationDetails(null);
      setExistingContract(null);
      return;
    }
    const fetchDetails = async () => {
      setLoading(true);
      try {
        // جلب تفاصيل الوحدة
        const unit = units.find(u => u.id === selectedUnit);
        setUnitDetails(unit);

        // جلب تفاصيل الحجز المرتبط
        const rid = (pickedRes && pickedRes.unit_id === selectedUnit) ? pickedRes.reservation_id : unit?.linked_reservation_id; // v3.4.590 — الحجز المختار
        if (rid) {
          const resRes = await fetch(`${API_BASE}/api/reservations/${rid}`, {
            credentials: "include"
          });
          const resData = await resRes.json();
          setReservationDetails(resData.reservation);
        } else {
          setReservationDetails(null);
        }

        // التحقق من وجود عقد سابق لهذه الوحدة
        const contractRes = await fetch(`${API_BASE}/api/contracts/unit/${selectedUnit}${rid ? `?reservation=${rid}` : ""}`, { /* v3.4.600 — عقد الحجز لا عقد الوحدة */
          credentials: "include"
        });
        if (contractRes.ok) {
          const contractData = await contractRes.json();
          setExistingContract(contractData.contract || null);
        } else {
          setExistingContract(null);
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchDetails();
  }, [selectedUnit, units]); // eslint-disable-line

  // جلب سجل العقود
  const fetchContracts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/contracts`, { credentials: "include" });
      const data = await res.json();
      setContracts(data.contracts || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "history") {
      fetchContracts();
    }
  }, [activeTab, fetchContracts]);

  // تحضير متغيرات العقد
  const prepareContractVariables = () => {
    if (!unitDetails || !reservationDetails) return null;

    const client = reservationDetails.linked_clients?.[0] || {};
    const today = new Date();
    const days = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

    return {
      Day: days[today.getDay()],
      Date: today.toLocaleDateString("ar-SA"),
      Name: client.name || reservationDetails.client_name || "",
      National_ID: client.national_id || "",
      Second_Mobile: client.phone || "",
      "Lot#": unitDetails.lot_number || "",
      "FlatType#": unitDetails.unit_label || unitDetails.unit_type || "",
      "Floor#": unitDetails.floor_number || "",
      "Front#": unitDetails.facade || "",
      "Area#": unitDetails.area_sqm || "",
      "Rooms#": unitDetails.rooms || "",
      "RestR#": unitDetails.bathrooms || "",
      "LivingR#": unitDetails.living_room || "",
      "Kitchen#": unitDetails.kitchen || "",
      "Maid#": unitDetails.maid_room ? "نعم" : "لا",
      "#Price": reservationDetails.value ? Number(reservationDetails.value).toLocaleString("en-US") : "",
      "#PriceR": numberToArabicWords(reservationDetails.value || 0),
      "#PaymentOption": unitDetails.payment_agreement || "نقداً",
      "#Plenty": "",
      ProjectName: projects.find(p => p.id === selectedProject)?.name || "",
      UnitLabel: unitDetails.unit_label || "",
    };
  };

  // إنشاء العقد
  // v3.4.528 — بناء نص العقد المباشر من القالب والقيم (يُستخدم للمعاينة الحيّة ولإنشاء المسودة)
  const buildDirectContent = (variables) => {
    let content;
    // v3.4.535 — إعدادات الصفحة الافتراضية كـ Google Docs: هوامش بوصة (96px) — القيم القديمة (قبل v2) تُؤخذ منها الخطوط فقط
    let pageSettings = { marginTop: 96, marginBottom: 96, marginLeft: 96, marginRight: 96, fontSize: 14, lineHeight: 1.5, paraSpacing: 8, fontFamily: "Traditional Arabic", v2: true };

    if (contractTemplate?.content) {
      content = applyVariablesToTemplate(contractTemplate.content, variables);
      // استخدام إعدادات الصفحة من القالب
      if (contractTemplate.variables?.pageSettings) {
        const tp = contractTemplate.variables.pageSettings;
        pageSettings = tp.v2 ? { ...pageSettings, ...tp } : { ...pageSettings, fontFamily: tp.fontFamily || pageSettings.fontFamily, fontSize: tp.fontSize || pageSettings.fontSize }; // v3.4.535
      }
    } else {
      // fallback للقالب القديم
      content = generateContractHTML(variables);
      pageSettings.legacy = true; // v3.4.578 — القالب المضمّن القديم يعتمد على تنسيق المعاينة الافتراضي
    }
    
    // تطبيق إعدادات الصفحة على المحتوى
    const styledContent = `<div style="
      font-family: '${pageSettings.fontFamily}', Arial, sans-serif;
      font-size: ${pageSettings.fontSize}px;
      line-height: ${pageSettings.lineHeight};
    ">${content}</div>`;
    
    return { styledContent, pageSettings };
  };
  // v3.4.528 — «إنشاء مسودة عقد»: من الحقول المُراجَعة مباشرة إلى Google Docs (التعديل والطباعة والاعتماد من اللوحة)
  const createDirectDraft = async (reviewed) => {
    if (!unitDetails || !reservationDetails) { toast.error("يجب اختيار وحدة مرتبطة بحجز"); return; }
    const variables = { ...prepareContractVariables(), ...(reviewed || {}) };
    const { styledContent, pageSettings } = buildDirectContent(variables);
    const full = { ...variables, _pageSettings: pageSettings };
    setContractContent(styledContent); setEditableContent(styledContent); setContractVariables(full);
    await createGoogleDraft("direct", full, styledContent);
  };
  const draftPreview = (showDraftPreview && directVars && unitDetails && reservationDetails) ? buildDirectContent({ ...prepareContractVariables(), ...directVars }) : null;

  // حفظ العقد
  const handleSaveContract = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/contracts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          unit_id: selectedUnit,
          reservation_id: effResId, // v3.4.590
          project_id: selectedProject,
          content: editableContent,
          variables: contractVariables,
        })
      });
      
      if (!res.ok) throw new Error("فشل حفظ العقد");
      
      toast.success("تم حفظ العقد بنجاح");
      setPreviewMode(false);
      setActiveTab("history");
      fetchContracts();
    } catch (e) {
      toast.error("فشل حفظ العقد");
    }
  };

  // طباعة العقد
  const handlePrint = () => {
    // استخراج إعدادات الصفحة
    const ps = contractVariables?._pageSettings || {
      marginTop: 20,
      marginBottom: 20,
      marginLeft: 25,
      marginRight: 25,
      fontSize: 14,
      lineHeight: 1.7,
      fontFamily: "Traditional Arabic"
    };

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>عقد بيع</title>
        <style>
          @page {
            size: A4;
            margin: ${ps.marginTop}mm ${ps.marginRight}mm ${ps.marginBottom}mm ${ps.marginLeft}mm;
          }
          
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          html, body {
            margin: 0;
            padding: 0;
            font-family: '${ps.fontFamily}', 'Simplified Arabic', 'Arial', sans-serif;
            font-size: ${ps.fontSize}px;
            line-height: ${ps.lineHeight};
            direction: rtl;
            color: #000;
            background: #fff;
          }
          
          .contract-content {
            padding: 0;
          }
          
          h1, h2, h3, h4 {
            color: #1e3a5f;
            margin: 12px 0 8px 0;
            page-break-after: avoid;
            break-after: avoid;
          }
          
          h1 { font-size: 20px; }
          h2 { font-size: 17px; }
          h3 { font-size: 15px; border-bottom: 1px solid #1e3a5f; padding-bottom: 4px; }
          
          p {
            margin: 6px 0;
            text-align: justify;
            orphans: 3;
            widows: 3;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 12px 0;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          th, td {
            border: 1px solid #333;
            padding: 6px 8px;
            text-align: center;
            font-size: 12px;
          }
          
          th {
            background: #f0f0f0 !important;
            font-weight: bold;
          }
          
          ul, ol {
            margin: 8px 0;
            padding-right: 20px;
          }
          
          li {
            margin: 4px 0;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* منع قطع البنود */
          .contract-section, .no-break {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* التوقيعات */
          .signatures {
            page-break-inside: avoid;
            break-inside: avoid;
            margin-top: 30px;
            padding-top: 15px;
            border-top: 2px solid #1e3a5f;
          }
          
          strong {
            color: #c00000;
          }
          
          /* معاينة على الشاشة */
          @media screen {
            html {
              background: #e0e0e0;
            }
            body {
              background: #fff;
              max-width: 210mm;
              min-height: 297mm;
              margin: 20px auto;
              padding: ${ps.marginTop}mm ${ps.marginRight}mm ${ps.marginBottom}mm ${ps.marginLeft}mm;
              box-shadow: 0 4px 20px rgba(0,0,0,0.2);
            }
          }
        </style>
      </head>
      <body>
        <div class="contract-content">
          ${editableContent}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  // عرض عقد من السجل
  const handleViewContract = (contract) => {
    setEditableContent(contract.content);
    setContractVariables(contract.variables || {});
    setExistingContract(contract);
    setActiveTab("create"); // Switch to create tab to show preview
    setPreviewMode(true);
  };

  // تحرير عقد موجود
  const handleEditExistingContract = (contract) => {
    setEditableContent(contract.content);
    setContractVariables(contract.variables || {});
    setPreviewMode(true);
  };

  // تحديث عقد موجود
  const handleUpdateContract = async () => {
    if (!existingContract) return;
    try {
      const res = await fetch(`${API_BASE}/api/contracts/${existingContract.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          content: editableContent,
          variables: contractVariables,
        })
      });
      
      if (!res.ok) throw new Error("فشل تحديث العقد");
      
      toast.success("تم تحديث العقد بنجاح");
      setPreviewMode(false);
      fetchContracts();
    } catch (e) {
      toast.error("فشل تحديث العقد");
    }
  };

  return (
    <div className="contracts-page">
      {/* v3.4.722 — التبويبات الرئيسية */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {MAIN_TABS.filter(([k]) => k !== "templates" || canEditTemplates).map(([k, l, c]) => <button key={k} type="button" onClick={() => pickMain(k)} style={{ padding: "9px 18px", borderRadius: 10, border: mainTab === k ? `2px solid ${c}` : "1px solid #e2e8f0", background: mainTab === k ? c + "14" : "#fff", color: mainTab === k ? c : "#475569", fontWeight: 800, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>)}
      </div>
      {mainTab === "templates" ? (/* v3.4.732 — تبويب القوالب */
        !canEditTemplates ? <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>تعديل القوالب يتطلب صلاحية «تعديل قوالب العقود»</div> : (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ background: "#fff", border: "1px solid #bfdbfe", borderInlineStart: "5px solid #2563eb", borderRadius: 14, padding: "12px 16px" }}>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>📄 قوالب عقود الشراء</div>
            <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 10 }}>العقد المباشر وعقد وافي: نص القالب، تعريف الحقول القابلة للتعديل قبل إصدار المسودة، وملف Word لقالب وافي.</div>
            <ContractTemplatesEditor onSaved={() => { invalidateTemplateConfig(); setTplVersion((n) => n + 1); }} />
          </div>
          <ResaleTemplateEditor kind="authorization" user={user} />
          <ResaleTemplateEditor kind="settlement" user={user} />
        </div>)
      ) : mainTab !== "purchase" ? <ResaleDocsTab kind={mainTab} user={user} /> : <>
      <div className="contracts-header">
        <h1><FiFileText /> إنشاء العقود</h1>
        <div className="tabs">
          <button className={activeTab === "create" ? "active" : ""} onClick={() => setActiveTab("create")}>
            <FiPlus /> إنشاء العقود
          </button>
          {/* v3.4.732 — «تعديل قوالب العقود» انتقل إلى التبويب الرئيسي «القوالب» */}
          <button className={activeTab === "history" ? "active" : ""} onClick={() => setActiveTab("history")}>
            <FiFileText /> سجل العقود السابقة
          </button>
        </div>
      </div>

      {activeTab === "create" && !previewMode && (
        <div className="create-contract">
          {/* v3.4.590 — إنشاء العقد يبدأ من اختيار حجز قائم (بدل المشروع ← الوحدة) — ويُفتح مباشرة من زر «إنشاء عقد» في تنسيق الحجوزات */}
          <div className="form-section">
            <h3>١. اختر الحجز</h3>
            {pickedRes ? (
              <div className="picked-res">
                <div className="pr-main">
                  <span className="pr-id">#{pickedRes.reservation_id}</span>
                  <b>{pickedRes.client_name || "—"}</b>
                  {pickedRes.client_phone && <span dir="ltr" className="pr-sub">{pickedRes.client_phone}</span>}
                </div>
                <div className="pr-meta">
                  <span>🏢 {pickedRes.project_name}</span>
                  <span>🔑 {pickedRes.unit_label || pickedRes.unit_number}{pickedRes.building_name ? ` — ${pickedRes.building_name}` : ""}</span>
                  {Number(pickedRes.value) > 0 && <span>💰 {Math.round(Number(pickedRes.value)).toLocaleString("en-US")}</span>}
                  {pickedRes.reservation_date && <span>📅 {new Date(pickedRes.reservation_date).toLocaleDateString("en-CA")}</span>}
                  {pickedRes.unit_status === "reserved_sakani" && <span className="pr-tag">محجوز سكني</span>}
                  {resCatChip(pickedRes)}{/* v3.4.591 */}
                  {pickedRes.quote_id && (() => { const x = resExtra(pickedRes); return <span className="pr-quote" title={pickedRes.quote_plan || ""}>🧾 عرض السعر {pickedRes.quote_no || "#" + pickedRes.quote_id}: {x.type || "—"}{x.first != null ? ` · مقدم ${fmtSar(x.first)}${x.firstPct != null ? ` (${x.firstPct}%)` : ""}` : ""}{x.n ? ` · ${x.n} دفعات` : ""}</span>; })()}{/* v3.4.596 */}
                </div>
                <button type="button" className="pr-change" onClick={clearPicked}>تغيير الحجز</button>
              </div>
            ) : (
              <div className="res-picker">
                {/* v3.4.591 — تابان رئيسيان: «حجوزات بانتظار إنشاء عقود» (بلا عقد / مسودة تحت الإجراء) و«عقود معتمدة» (بانتظار التوقيع / معتمدة وموقّعة) */}
                <div className="rp-main-tabs">
                  {RES_TABS.map(([k, label, subs]) => { const n = subs.reduce((s, [sk]) => s + (resCounts[sk] || 0), 0); return (
                    <button type="button" key={k} className={resTab === k ? "on" : ""} onClick={() => { setResTab(k); setResSub("all"); }}>
                      {label} <span className="cnt">{n}</span>
                    </button>); })}
                </div>
                <div className="rp-sub-tabs">
                  <button type="button" className={resSub === "all" ? "on" : ""} onClick={() => setResSub("all")}>الكل <span className="cnt">{tabCats.reduce((s, k) => s + (resCounts[k] || 0), 0)}</span></button>{/* v3.4.592 */}
                  {(RES_TABS.find(t => t[0] === resTab) || RES_TABS[0])[2].map(([sk, sl]) => (
                    <button type="button" key={sk} className={resSub === sk ? "on " + sk : sk} onClick={() => setResSub(sk)}>{sl} <span className="cnt">{resCounts[sk] || 0}</span></button>
                  ))}
                </div>
                <input value={resQ} onChange={e => setResQ(e.target.value)} placeholder="🔍 ابحث برقم الحجز أو اسم العميل أو الجوال أو المشروع أو الوحدة…" />
                <div className="rp-head">{/* v3.4.596 */}
                  <span>رقم الحجز</span><span>الإجراء</span><span>العميل</span><span>المشروع</span><span>الوحدة</span>
                  <span className="rp-x">نوع العقد</span><span className="rp-x">الدفعة المقدمة</span><span className="rp-x">عدد الدفعات</span><span className="rp-x">عرض السعر</span><span>حالة العقد</span><span className="rp-del">حذف</span>{/* v3.4.629 */}
                </div>
                <div className="rp-list">
                  {resList === null ? <div className="rp-empty">جاري تحميل الحجوزات…</div>
                    : shownRes.length === 0 ? <div className="rp-empty">{resQ.trim() ? "لا يوجد حجز مطابق في هذه القائمة" : "لا توجد حجوزات في هذه القائمة"}</div>
                    : shownRes.map(r => (
                      <div role="button" tabIndex={0} key={r.reservation_id + "_" + r.unit_id} className="rp-row" onClick={() => pickRes(r)} onKeyDown={(e) => { if (e.key === "Enter") pickRes(r); }}>
                        <span className="pr-id">#{r.reservation_id}</span>
                        {/* v3.4.595 — عمود الإجراء: إنشاء عقد / تعديل مسودة (يفتح التعديل الإلكتروني مباشرة) / فتح العقد */}
                        {(() => { const k = resCat(r); return (
                          <button type="button" className={"rp-act " + k} onClick={(e) => { e.stopPropagation(); pickRes(r, k === "draft"); }}>
                            {k === "none" ? "＋ إنشاء عقد" : k === "draft" ? "✏️ تعديل المسودة" : "📄 فتح العقد"}
                          </button>); })()}
                        <span className="rp-client">{r.client_name || "—"}</span>
                        <span className="rp-proj">{r.project_name}</span>
                        <span className="rp-unit">{r.unit_label || r.unit_number}</span>
                        {(() => { const x = resExtra(r); return (<>
                          <span className="rp-x rp-ct" title={x.typeFrom}>{x.type || "—"}</span>
                          <span className="rp-x rp-num" title={x.first != null ? (r.quote_plan || "") : x.expected != null ? "الدفعة الأولى المتوقعة (من تنسيق الحجوزات)" : ""}>
                            {x.first != null ? <>{fmtSar(x.first)}{x.firstPct != null && <small> ({x.firstPct}%)</small>}</> : x.expected != null ? <span className="rp-exp">{fmtSar(x.expected)} <small>متوقعة</small></span> : "—"}
                          </span>
                          <span className="rp-x rp-num">{x.n != null ? x.n : "—"}</span>
                          <span className="rp-x rp-quote" title={r.quote_plan || ""}>{r.quote_id ? <>{r.quote_no || "#" + r.quote_id}<small className={"qs " + (r.quote_status || "")}>{{ draft: "مسودة", pending: "بانتظار الاعتماد", approved: "معتمد" }[r.quote_status] || r.quote_status || ""}</small></> : "—"}</span>
                        </>); })()}
                        {resCatChip(r)}
                        {/* v3.4.629 — حذف المسودة (المسودات فقط) */}
                        <span className="rp-del">{resCat(r) === "draft" && canDelDraft ? <button type="button" title="حذف مسودة العقد" onClick={(e) => { e.stopPropagation(); deleteDraft(r); }} className="rp-del-btn">🗑</button> : null}</span>
                      </div>
                    ))}
                  {resList && subRes.length > shownRes.length && <div className="rp-empty">يُعرض {shownRes.length} من {subRes.length} — ضيّق البحث</div>}
                </div>
              </div>
            )}
          </div>

          {pickedRes && selectedProject && !loading && units.length > 0 && !units.some(u => u.id === pickedRes.unit_id) && (
            <div className="error-box"><FiX size={20} /><span>تعذّر العثور على وحدة هذا الحجز في المشروع — حدّث الصفحة أو راجع الوحدة</span></div>
          )}

          {selectedUnit && loading && (
            <div className="loading">جاري تحميل البيانات...</div>
          )}

          {selectedUnit && !loading && unitDetails && (
            <div className="unit-details">
              <h3>٢. العقد وبيانات الحجز</h3>
              
              {!effResId ? (
                <div className="error-box">
                  <FiX size={24} />
                  <span>لا يمكن إنشاء عقد - الوحدة غير مرتبطة بحجز</span>
                </div>
              ) : (
                <>
                  {unitMismatch && (/* v3.4.600 */
                    <div className="error-box" style={{ marginBottom: 10 }}><FiX size={20} /><span>هذه المسودة أُنشئت على وحدة أخرى{pickedRes?.contract_unit_name ? ` («${pickedRes.contract_unit_name}»)` : ""} قبل تغيير وحدة الحجز. اضغط «تعديل إلكتروني للعقد» ثم «حفظ المسودة من جديد» لتحديث بيانات الوحدة وربط العقد بالوحدة الحالية — لا يمكن اعتماده قبل ذلك.</span></div>
                  )}
                  {/* تنبيه العقد الموجود */}
                  {existingContract && (
                    <div className="existing-contract-alert">
                      <div className="alert-content">
                        <FiFileText size={24} />
                        <div>
                          <strong>{existingContract.status === "draft" ? "يوجد مسودة عقد لهذه الوحدة" : existingContract.status === "approved" ? "يوجد عقد معتمد لهذه الوحدة" : "يوجد عقد سابق لهذه الوحدة"}</strong>{/* v3.4.591 */}
                          <p>تم إنشاؤه بتاريخ {new Date(existingContract.created_at).toLocaleDateString("ar-SA")}</p>
                        </div>
                      </div>
                      {!existingContract.google_doc_id && <button className="btn-edit-existing" onClick={() => handleEditExistingContract(existingContract)}>
                        <FiEdit2 /> تحرير العقد الموجود
                      </button>}
                    </div>
                  )}
                  {existingContract?.google_doc_id && <GoogleDocPanel contract={existingContract} canApprove={canApprove} onEditFields={reEdit ? null : startReEdit} onChange={(c) => { if (c) setExistingContract(c); else refreshExisting(); }} />}{/* v3.4.523 */}
                  {existingContract && !reEdit && <VariablesReview key={"vr" + existingContract.id} format={existingContract.format || "direct"} variables={existingContract.variables}
                    renderPreview={existingContract.format === "wafi"
                      ? () => <DocxPreview source={() => contractsGoogle.wafiDocx(existingContract.variables || {})} deps={[existingContract.id, existingContract.updated_at]} />
                      : (existingContract.content ? () => <div className="contract-preview" style={{ margin: 0 }}><PaginatedPreview content={existingContract.content} pageSettings={existingContract.variables?._pageSettings} readOnly /></div> : null)} />}{/* v3.4.598 — أقسام قابلة للطي + قسم المعاينة */}{/* v3.4.525 — كل الحقول المتغيّرة للمراجعة قبل الاعتماد أو التحرير */}

                  {/* v3.4.600 — بيانات المصدر الحالية في النظام (تُملأ منها حقول العقد عند الإنشاء) — للمقارنة مع حقول العقد أعلاه */}
                  <div className="src-fold">
                    <button type="button" className={"src-head" + (srcOpen.unit ? " on" : "")} onClick={() => setSrcOpen((o) => ({ ...o, unit: !o.unit }))}>
                      <span className="arr">◀</span><b>🏠 بيانات الوحدة في النظام</b><small>{unitDetails.unit_label} · {unitDetails.unit_type || "—"} · {unitDetails.area_sqm ? unitDetails.area_sqm + " م²" : "—"} — المصدر الذي تُملأ منه حقول العقار</small>
                    </button>
                  {srcOpen.unit && <div className="details-grid">
                    <div className="detail-item">
                      <label>رقم الوحدة</label>
                      <span>{unitDetails.unit_label}</span>
                    </div>
                    <div className="detail-item">
                      <label>نوع الوحدة</label>
                      <span>{unitDetails.unit_type}</span>
                    </div>
                    <div className="detail-item">
                      <label>الدور</label>
                      <span>{unitDetails.floor_number}</span>
                    </div>
                    <div className="detail-item">
                      <label>المساحة</label>
                      <span>{unitDetails.area_sqm} م²</span>
                    </div>
                    <div className="detail-item">
                      <label>الواجهة</label>
                      <span>{unitDetails.facade || "-"}</span>
                    </div>
                    <div className="detail-item">
                      <label>عدد الغرف</label>
                      <span>{unitDetails.rooms || "-"}</span>
                    </div>
                    <div className="detail-item">
                      <label>السعر الرسمي</label>
                      <span>{unitDetails.price ? Number(unitDetails.price).toLocaleString("en-US") + " ر.س" : "-"}</span>
                    </div>
                  </div>}
                  </div>

                  {reservationDetails && (
                    <>
                      <div className="src-fold">
                        <button type="button" className={"src-head" + (srcOpen.res ? " on" : "")} onClick={() => setSrcOpen((o) => ({ ...o, res: !o.res }))}>
                          <span className="arr">◀</span><b>👤 بيانات الحجز والعميل في النظام</b><small>{reservationDetails.linked_clients?.[0]?.name || reservationDetails.client_name || "—"} · {reservationDetails.value ? Number(reservationDetails.value).toLocaleString("en-US") + " ر.س" : "—"} — المصدر الذي تُملأ منه بيانات المشتري والثمن</small>
                        </button>
                      {srcOpen.res && <div className="details-grid">
                        <div className="detail-item">
                          <label>اسم العميل</label>
                          <span>{reservationDetails.linked_clients?.[0]?.name || reservationDetails.client_name}</span>
                        </div>
                        <div className="detail-item">
                          <label>سعر البيع</label>
                          <span>{reservationDetails.value ? Number(reservationDetails.value).toLocaleString("en-US") + " ر.س" : "-"}</span>
                        </div>
                        <div className="detail-item">
                          <label>العربون</label>
                          <span>{reservationDetails.deposit_amount ? Number(reservationDetails.deposit_amount).toLocaleString("en-US") + " ر.س" : "-"}</span>
                        </div>
                      </div>}
                      </div>

                      {(!existingContract || reEdit) && (
                        <>
                          {/* v3.4.523 — اختيار الصيغة: مباشر (معاينة A4 ثم Google Docs) أو وافي (نموذج ← Google Docs) */}
                          {reEdit ? (/* v3.4.591 */
                            <div className="reedit-bar">
                              <div><b>🧾 تعديل إلكتروني لمسودة العقد</b><span>عدّل الحقول ثم «حفظ المسودة من جديد» — يُحدَّث نفس مستند Google ونفس الرابط</span></div>
                              <button type="button" onClick={() => setReEdit(false)} disabled={gBusy}>إلغاء التعديل</button>
                            </div>
                          ) : <>
                            <h3 style={{ marginTop: 18 }}>٣. صيغة العقد</h3>
                            <FormatPicker value={format} onChange={setFormat} />
                          </>}
                          {format === "direct" ? (
                            <>
                              <DirectForm key={"d" + selectedUnit + "_" + tplVersion + (reEdit ? "_e" : "") + "_" + (approvedQuoteId || "")} quoteId={approvedQuoteId} submitLabel={reEdit ? "💾 حفظ المسودة من جديد" : undefined} initial={reEdit ? { ...(prepareContractVariables() || {}), ...draftVars(), ...freshUnit("direct", prepareContractVariables()), ...freshLocked("direct", prepareContractVariables()) } : (prepareContractVariables() || {})} busy={gBusy} onChange={setDirectVars} onSubmit={(v) => createDirectDraft(v)} onPreview={() => setShowDraftPreview((s) => !s)} previewOpen={showDraftPreview} />
                              {showDraftPreview && (/* v3.4.528 — معاينة حيّة للمسودة (قراءة فقط) تتحدّث مع تغيير الحقول */
                                <div className="contract-preview" style={{ marginTop: 16 }}>
                                  <div className="preview-header">
                                    <h2>معاينة مسودة العقد — حجم A4 (قراءة فقط)</h2>
                                    <div className="preview-actions"><span style={{ fontSize: 12, color: "#64748b" }}>تتحدّث تلقائيًا مع تعديل الحقول أعلاه</span></div>
                                  </div>
                                  <PaginatedPreview content={draftPreview?.styledContent || ""} pageSettings={draftPreview?.pageSettings} readOnly />
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <WafiForm key={"w" + selectedUnit + "_" + tplVersion + (reEdit ? "_e" : "") + "_" + (approvedQuoteId || "")} quoteId={approvedQuoteId} submitLabel={reEdit ? "💾 حفظ المسودة من جديد" : undefined} projectId={selectedProject} initial={reEdit ? { ...prepareWafiVariables(), ...draftVars(), ...freshUnit("wafi", prepareWafiVariables()), ...freshLocked("wafi", prepareWafiVariables()) } : prepareWafiVariables()} busy={gBusy || wafiBusy} onSubmit={(v) => createGoogleDraft("wafi", v)} onPreview={previewWafi} previewOpen={!!wafiPreview} />
                              {wafiPreview && (/* v3.4.538 — معاينة تقريبية لقالب Word بالقيم الحالية */
                                <div className="contract-preview" style={{ marginTop: 16 }}>
                                  <div className="preview-header">
                                    <h2>معاينة مسودة عقد وافي — حجم A4 (قراءة فقط)</h2>
                                    <div className="preview-actions"><span style={{ fontSize: 12, color: "#64748b" }}>بتنسيق ملف Word (قالب وافي) نفسه — النسخة النهائية في Google Docs</span></div>
                                  </div>
                                  <DocxPreview source={() => contractsGoogle.wafiDocx(wafiPreview.vars)} deps={[wafiPreview.at]} />{/* v3.4.557 */}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "create" && previewMode && (
        <div className="contract-preview">
          <div className="preview-header">
            <h2>{existingContract ? "تحرير العقد" : "معاينة العقد"} — حجم A4</h2>
            <div className="preview-actions">
              <button onClick={() => { setPreviewMode(false); setExistingContract(null); }}><FiX /> رجوع</button>
              <button onClick={handlePrint}><FiPrinter /> طباعة</button>
              {!existingContract && <button className="btn-save" disabled={gBusy} onClick={() => createGoogleDraft("direct", contractVariables, editableContent)} title="ينشئ مستند Google من هذا النص للتعديل الحر ثم الاعتماد" style={{ background: "#2563eb" }}>{gBusy ? "…" : "✏️ تعديل في Google Docs"}</button>}{/* v3.4.523 */}
              {existingContract ? (
                <button className="btn-save" onClick={handleUpdateContract}><FiCheck /> تحديث العقد</button>
              ) : (
                <button className="btn-save" onClick={handleSaveContract}><FiCheck /> حفظ العقد</button>
              )}
            </div>
          </div>
          <VariablesReview format="direct" variables={contractVariables} defaultOpen={false} />{/* v3.4.525 — حقول العقد مع المعاينة/التحرير */}
          <PaginatedPreview 
            content={editableContent} 
            onContentChange={setEditableContent}
            pageSettings={contractVariables?._pageSettings}
          />
        </div>
      )}

      {activeTab === "templates" && canEditTemplates && (/* v3.4.532 */
        <ContractTemplatesEditor onSaved={() => { invalidateTemplateConfig(); setTplVersion((n) => n + 1); }} />
      )}

      {activeTab === "history" && (
        <div className="contracts-history">
          <h2>سجل العقود</h2>
          {contracts.length === 0 ? (
            <div className="no-contracts">لا توجد عقود محفوظة</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>المشروع</th>
                  <th>الوحدة</th>
                  <th>العميل</th>
                  <th>الصيغة</th>{/* v3.4.523 */}
                  <th>الحالة</th>
                  <th>تاريخ الإنشاء</th>
                  <th>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c, i) => (
                  <tr key={c.id}>
                    <td>{i + 1}</td>
                    <td>{c.project_name}</td>
                    <td>{c.unit_label}</td>
                    <td>{c.client_name}</td>
                    <td>{FORMAT_LABEL[c.format] || "مباشر"}</td>
                    <td>{(() => { const st = STATUS_LABEL[c.status] || STATUS_LABEL.active; return <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, background: st[1], color: st[2] }}>{st[0]}</span>; })()}</td>
                    <td>{new Date(c.created_at).toLocaleDateString("ar-SA")}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {c.google_doc_id ? (<>{/* v3.4.523 — عقود Google Docs */}
                        <button onClick={async () => { try { await contractsGoogle.share(c.id); } catch (_) { /* */ } window.open(c.google_doc_link, "_blank", "noopener"); }}>✏️ المستند</button>{" "}
                        <a href={pdfUrl(c.id)} target="_blank" rel="noreferrer"><button>🖨 PDF</button></a>{" "}
                        {c.status !== "approved" && canApprove && <button onClick={async () => { if (!window.confirm("اعتماد العقد وحفظ PDF في مستندات الوحدة؟")) return; try { await contractsGoogle.approve(c.id); toast.success("✅ اعتُمد"); fetchContracts(); } catch (e) { toast.error(e.message); } }}>✅ اعتماد</button>}
                        {c.status === "approved" && c.pdf_link && <a href={c.pdf_link} target="_blank" rel="noreferrer"><button>📄 المعتمد</button></a>}
                      </>) : (
                        <button onClick={() => handleViewContract(c)}><FiEye /> عرض</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      </>}{/* v3.4.722 */}
    </div>
  );
}

// تحويل الأرقام إلى كلمات عربية
function numberToArabicWords(num) {
  if (!num || num === 0) return "صفر";
  
  const ones = ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة"];
  const tens = ["", "عشرة", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"];
  const hundreds = ["", "مائة", "مائتان", "ثلاثمائة", "أربعمائة", "خمسمائة", "ستمائة", "سبعمائة", "ثمانمائة", "تسعمائة"];
  
  const n = Math.floor(Number(num));
  // v3.4.527 — تمييز عربي كامل للآلاف والملايين (مثال: 310,000 → ثلاثمائة وعشرة آلاف) بدل «310 ألف»
  const scale = (count, one, two, few, many) => {
    if (count === 1) return one;
    if (count === 2) return two;
    const r = count % 100;
    if (r >= 3 && r <= 10) return numberToArabicWords(count) + " " + few;
    if ((r === 1 || r === 2) && count > 2) return numberToArabicWords(count - r) + " و" + (r === 1 ? one : two);
    return numberToArabicWords(count) + " " + many;
  };
  if (n >= 1000000) {
    const millions = Math.floor(n / 1000000), remainder = n % 1000000;
    let result = scale(millions, "مليون", "مليونان", "ملايين", "مليون");
    if (remainder > 0) result += " و" + numberToArabicWords(remainder);
    return result;
  }
  if (n >= 1000) {
    const thousands = Math.floor(n / 1000);
    const remainder = n % 1000;
    let result = scale(thousands, "ألف", "ألفان", "آلاف", "ألف");
    if (remainder > 0) result += " و" + numberToArabicWords(remainder);
    return result;
  }
  if (n >= 100) {
    const h = Math.floor(n / 100);
    const remainder = n % 100;
    let result = hundreds[h];
    if (remainder > 0) result += " و" + numberToArabicWords(remainder);
    return result;
  }
  if (n >= 20) {
    const t = Math.floor(n / 10);
    const o = n % 10;
    if (o === 0) return tens[t];
    return ones[o] + " و" + tens[t];
  }
  if (n >= 10) {
    const special = ["عشرة", "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"];
    return special[n - 10];
  }
  return ones[n];
}

// إنشاء HTML للعقد
// تطبيق المتغيرات على القالب
function applyVariablesToTemplate(template, vars) {
  // v3.4.525 — إزالة الوسوم داخل {{…}} حتى يُستبدل العنصر النائب (قالب Word يقطّع {{#Price}} بوسوم span)
  let content = String(template || "").replace(/\{\{(?:[^{}<]|<[^>]*>)*\}\}/g, (m) => m.replace(/<[^>]*>/g, ""));
  
  // إنشاء قائمة بجميع الاستبدالات الممكنة
  const replacements = {};
  Object.keys(vars).forEach(key => {
    const value = vars[key] || "";
    // إضافة جميع الصيغ الممكنة للمفتاح
    replacements[`{{${key}}}`] = value;
    replacements[`{{#${key}}}`] = value;
    // إذا كان المفتاح يبدأ بـ # ، أضف بدونه أيضاً
    if (key.startsWith('#')) {
      replacements[`{{${key.substring(1)}}}`] = value;
      replacements[`{{${key}}}`] = value;
    }
  });
  
  // استبدال جميع المتغيرات
  Object.keys(replacements).forEach(pattern => {
    content = content.split(pattern).join(replacements[pattern]);
  });
  
  // إزالة المتغيرات المميزة من المحرر الغني (spans)
  content = content.replace(/<span class="template-variable"[^>]*>([^<]+)<\/span>/g, (match, varKey) => {
    const cleanKey = varKey.replace(/[{}]/g, '').trim();
    return vars[cleanKey] || vars[cleanKey.replace('#', '')] || vars[`#${cleanKey}`] || "";
  });
  
  // إزالة أي متغيرات متبقية لم يتم استبدالها
  content = content.replace(/\{\{#?[^}]+\}\}/g, '');
  
  return content;
}

function generateContractHTML(vars) {
  return `
    <div style="text-align: center; margin-bottom: 30px;">
      <h1 style="color: #1e3a5f;">عقد بيع وحدة سكنية</h1>
      <p>تم بعون الله وتوفيقه إبرام هذا العقد في يوم: <strong>${vars.Day}</strong> الموافق <strong>${vars.Date}</strong></p>
    </div>

    <div style="margin-bottom: 20px;">
      <p><strong>الطرف الأول:</strong> شركة رئال للتطوير العقاري - بسجل تجاري رقم: 4030459357 - عنوانها: جدة - حي الواحة</p>
      <p><strong>الطرف الثاني:</strong> ${vars.Name} - رقم الهوية: ${vars.National_ID} - رقم الجوال: ${vars.Second_Mobile}</p>
    </div>

    <h3>البند الأول: تفاصيل العقار</h3>
    <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
      <tr>
        <th style="border: 1px solid #ccc; padding: 8px; background: #f0f0f0;">رقم الشقة</th>
        <th style="border: 1px solid #ccc; padding: 8px; background: #f0f0f0;">الدور</th>
        <th style="border: 1px solid #ccc; padding: 8px; background: #f0f0f0;">الواجهة</th>
        <th style="border: 1px solid #ccc; padding: 8px; background: #f0f0f0;">المساحة</th>
        <th style="border: 1px solid #ccc; padding: 8px; background: #f0f0f0;">عدد الغرف</th>
      </tr>
      <tr>
        <td style="border: 1px solid #ccc; padding: 8px; text-align: center;">${vars["FlatType#"]}</td>
        <td style="border: 1px solid #ccc; padding: 8px; text-align: center;">${vars["Floor#"]}</td>
        <td style="border: 1px solid #ccc; padding: 8px; text-align: center;">${vars["Front#"]}</td>
        <td style="border: 1px solid #ccc; padding: 8px; text-align: center;">${vars["Area#"]} م²</td>
        <td style="border: 1px solid #ccc; padding: 8px; text-align: center;">${vars["Rooms#"]}</td>
      </tr>
    </table>

    <h3>البند الثاني: قيمة العقار</h3>
    <p>اشترى وتملك الطرف الثاني الوحدة السكنية المذكورة أعلاه بمبلغ وقدره (<strong>${vars["#Price"]} ريال سعودي</strong>) (${vars["#PriceR"]} فقط لا غير).</p>

    <h3>البند الثالث: طريقة الدفع</h3>
    <p>${vars["#PaymentOption"]}</p>

    <h3>البند الرابع: الضمانات</h3>
    <ul>
      <li>ضمان لمدة 15 سنة هيكل البناء</li>
      <li>ضمان لمدة 2 سنوات أعمال السباكة والكهرباء</li>
      <li>ضمان لمدة 5 سنوات المصاعد</li>
    </ul>

    <div style="display: flex; justify-content: space-between; margin-top: 60px; padding-top: 20px; border-top: 1px solid #ccc;">
      <div style="width: 45%; text-align: center;">
        <p><strong>الطرف الأول</strong></p>
        <p>شركة رئال للتطوير العقاري</p>
        <p style="margin-top: 40px;">التوقيع: ..................</p>
      </div>
      <div style="width: 45%; text-align: center;">
        <p><strong>الطرف الثاني</strong></p>
        <p>${vars.Name}</p>
        <p style="margin-top: 40px;">التوقيع: ..................</p>
      </div>
    </div>
  `;
}

// ══════════════════════════════════════════════════════════════
// مكون معاينة الصفحات المتعددة
// ══════════════════════════════════════════════════════════════
function PaginatedPreview({ content, onContentChange, readOnly = false, pageSettings }) {
  // v3.4.535 — الهوامش والخط من إعدادات صفحة القالب (نفس قيم المحرر ومستند Google) — الهامش العلوي/السفلي يشمل شريطي الترويسة والتذييل
  const ps = { marginTop: 96, marginBottom: 96, marginLeft: 96, marginRight: 96, fontSize: 14, lineHeight: 1.5, ...(pageSettings && pageSettings.v2 ? pageSettings : {}) };
  const [pages, setPages] = useState([]);
  const measureRef = React.useRef(null);
  
  // أبعاد صفحة A4 بالبكسل (96 DPI)
  const PAGE_WIDTH = 794; // 210mm
  const PAGE_HEIGHT = 1123; // 297mm
  const HEADER_HEIGHT = ps.headerOn ? 34 : 0; // v3.4.536 — الترويسة/التذييل من إعدادات القالب (كما في Google Docs: لا شيء افتراضيًا)
  const FOOTER_HEIGHT = ps.footerOn ? 30 : 0;
  const MARGIN_TOP = Math.max(8, Number(ps.marginTop) - HEADER_HEIGHT);
  const MARGIN_BOTTOM = Math.max(8, Number(ps.marginBottom) - FOOTER_HEIGHT);
  const MARGIN_L = Number(ps.marginLeft) || 96, MARGIN_R = Number(ps.marginRight) || 96;
  const CONTENT_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;
  // v3.4.578 — قالب من المحرر: تُعرض الفقرات والجداول بتنسيق القالب نفسه (مثل المحرر) بدل أنماط المعاينة الافتراضية
  const exact = !!(pageSettings && pageSettings.v2 && !pageSettings.legacy);
  const paraGap = `${Number(ps.paraSpacing ?? 8) || 0}px`;

  useEffect(() => {
    paginateContent();
  }, [content, pageSettings]);

  const paginateContent = () => {
    if (!measureRef.current) return;
    
    // إنشاء عنصر مؤقت للقياس
    const tempDiv = document.createElement('div');
    tempDiv.style.cssText = `
      position: absolute;
      visibility: hidden;
      width: ${PAGE_WIDTH - MARGIN_L - MARGIN_R}px;
      font-family: '${ps.fontFamily || "Traditional Arabic"}', Arial, sans-serif;
      font-size: ${ps.fontSize || 14}px;
      line-height: ${ps.lineHeight || 1.5};
      direction: rtl;
    `;
    if (exact) { tempDiv.className = "tpl-measure"; tempDiv.style.setProperty("--tpl-para", paraGap); } // v3.4.578 — القياس بنفس تباعد الفقرات
    tempDiv.innerHTML = content; // v3.4.535 — بلا تعقيم: الهوامش السالبة من Word تُستوعب داخل هوامش البوصة كما في Google Docs
    document.body.appendChild(tempDiv);

    // v3.4.530 — المحتوى ملفوف بـ div واحد (إعدادات الخط) وأحيانًا WordSection → كان يُعدّ عنصرًا واحدًا فتظهر صفحة واحدة فقط؛ نفكّ الأغلفة ونحمل تنسيقها لكل صفحة
    let root = tempDiv; let wrapStyle = "";
    while (root.children.length === 1 && root.children[0].tagName === "DIV" && root.children[0].children.length > 0) { wrapStyle += (root.children[0].getAttribute("style") || "") + ";"; root = root.children[0]; }
    const wrap = (html) => (wrapStyle.replace(/;+/g, "").trim() ? `<div style="${wrapStyle.replace(/"/g, "'")}">${html}</div>` : html);
    // v3.4.594 — القياس بمواضع الكتل الفعلية (كان يضيف 10px لكل فقرة فتنكسر الصفحة مبكرًا) + فاصل Word — مطابق لعلامات المحرر
    const pagesContent = paginateBlocks(root, CONTENT_HEIGHT).map((p) => wrap(p.blocks.map((el) => el.outerHTML).join("")));

    document.body.removeChild(tempDiv);
    
    // على الأقل صفحة واحدة
    if (pagesContent.length === 0) {
      pagesContent.push(content);
    }

    setPages(pagesContent);
  };

  const handlePageEdit = (pageIndex, newContent) => {
    const newPages = [...pages];
    newPages[pageIndex] = newContent;
    
    // إعادة دمج كل الصفحات
    const fullContent = newPages.join('');
    onContentChange(fullContent);
  };

  return (
    <div className="paginated-preview-container" ref={measureRef}>
      <div className="pages-info">
        📄 عدد الصفحات: {pages.length}
      </div>
      <div className="pages-wrapper">
        {pages.map((pageContent, index) => (
          <div key={index} className="a4-page">
            {/* الهيدر */}
            {ps.headerOn && <div className="page-header" style={{ height: HEADER_HEIGHT, background: "none", color: "#475569", padding: `0 ${MARGIN_R}px 0 ${MARGIN_L}px`, borderBottom: "1px solid #e2e8f0", fontSize: 11, justifyContent: "flex-start" }}>{/* v3.4.536 */}
              <div className="header-content"><span>{ps.headerText || ""}</span></div>
            </div>}
            
            {/* المحتوى */}
            <div 
              className={exact ? "page-content tpl-exact" : "page-content"}
              contentEditable={!readOnly}
              dir="rtl"
              dangerouslySetInnerHTML={{ __html: pageContent }}
              onBlur={readOnly ? undefined : (e => handlePageEdit(index, e.target.innerHTML))}
              style={{ minHeight: CONTENT_HEIGHT, padding: `${MARGIN_TOP}px ${MARGIN_R}px ${MARGIN_BOTTOM}px ${MARGIN_L}px`, overflowX: "hidden", wordWrap: "break-word", "--tpl-para": paraGap }} // v3.4.535 — هوامش من إعدادات القالب
            />
            
            {/* الفوتر */}
            {ps.footerOn && <div className="page-footer" style={{ height: FOOTER_HEIGHT, padding: `0 ${MARGIN_R}px 0 ${MARGIN_L}px`, justifyContent: "space-between", display: "flex", alignItems: "center", borderTop: "1px solid #e2e8f0", background: "none" }}>
              <span>{ps.footerText || ""}</span>{ps.pageNumbers && <span>صفحة {index + 1} من {pages.length}</span>}
            </div>}
          </div>
        ))}
      </div>
    </div>
  );
}
